import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import {
    ReactiveFormsModule,
    UntypedFormBuilder,
    FormGroup,
    Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ReportService } from '../../shared/services/report.service';
import { VenueService } from '../../shared/services/venue.service';
import { BookingService } from '../bookings/booking.service';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { BookingComponent } from '../bookings/booking/booking.component';
import { BOOKING_DIALOG_CONFIG } from '../bookings/booking/booking-dialog.config';
import { formatBookingStatusLabel } from '../../shared/booking-status-label';
import { formatAedCurrency } from '../../shared/aed-currency';
import { useVenueWallClockForDisplay } from '../../shared/venue-wall-clock';
import { formatProductDate } from '../../shared/product-date';

@Component({
    selector: 'app-report',
    imports: [
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatTableModule,
        MatPaginatorModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        MatDatepickerModule,
        MatOptionModule,
        MatSelectModule,
        MatDialogModule
    ],
    providers: [provideNativeDateAdapter()],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './report.component.html',
    styleUrl: './report.component.scss'
})
export class ReportComponent implements OnInit {
    readonly formatBookingStatusLabel = formatBookingStatusLabel;
    readonly formatAedCurrency = formatAedCurrency;
    readonly formatProductDate = formatProductDate;
    readonly dialog = inject(MatDialog);
    reportForm: FormGroup;
    isLoading: boolean = false;
    hasSearched: boolean = false;
    venues: any[] = []; 
    courts: any[] = []; 
    isCourtsLoading: boolean = false; 
    isVenuesLoading: boolean = false; 

    // Table properties
    displayedColumns: string[] = ['bookingId', 'venue', 'courtId', 'start', 'durationHours', 'total', 'status', 'actions'];
    dataSource = new MatTableDataSource<any>([]);
    totalItems: number = 0;
    
    // Pagination properties
    pageSize: number = 5;
    pageIndex: number = 0;
    pageSizeOptions: number[] = [5, 10, 20, 50, 100];

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private reportService: ReportService,
        private venueService: VenueService,
        private bookingService: BookingService,
        private cdr: ChangeDetectorRef
    ) {
        const today = new Date();
        const month = today.getMonth();
        const year = today.getFullYear();
        this.reportForm = this._formBuilder.group({
            venueId: [''],
            courtId: [''],
            status: [''],
            startDate: [new Date(year, month, 1)],
            endDate: [new Date(year, month, 29)],
        });
    }

    ngOnInit(): void {
        this.fetchVenues();
        this.onSubmit();
    }

    fetchVenues(): void {
        this.isVenuesLoading = true;
        this.venueService.getVenues().subscribe({
            next: (response) => {
                this.isVenuesLoading = false;
                this.venues = response || [];
            },
            error: (error) => {
                this.isVenuesLoading = false;
                console.error('Error fetching venues:', error);
                this.venues = [];
            }
        });
    }

    onVenueChange(venueId: string): void {
        if (venueId) {
            this.isCourtsLoading = true;
            this.courts = [];
            this.reportForm.get('courtId')?.setValue('');
            this.resetPaginationAndSearch();
            this.venueService.getCourtsByVenue(venueId).subscribe({
                next: (response) => {
                    this.isCourtsLoading = false;
                    console.log('Courts API response:', response);

                    if (response && response.success && Array.isArray(response.results)) {
                        this.courts = response.results;
                    } else if (Array.isArray(response)) {
                        this.courts = response;
                    } else {
                        this.courts = [];
                    }

                    this.cdr.markForCheck();
                    console.log('Processed courts:', this.courts);
                },
                error: (error) => {
                    this.isCourtsLoading = false;
                    console.error('Error fetching courts:', error);
                    this.courts = [];
                }
            });
        } else {
            this.courts = [];
            this.reportForm.get('courtId')?.setValue('');
            this.resetPaginationAndSearch();
        }
    }

    onDateChange(event: any): void {
        this.resetPaginationAndSearch();
    }

    resetPaginationAndSearch(): void {
        this.pageIndex = 0;
        this.onSubmit();
    }

    onSubmit(): void {
        this.isLoading = true;
        const formData = {
            ...this.reportForm.value,
            page: this.pageIndex + 1,  
            limit: this.pageSize 
        };

        this.reportService.searchBookings(formData).subscribe({
            next: (response: any) => {
                this.isLoading = false;
                this.hasSearched = true;
                const results = response.results ?? [];
                const total = response.total ?? results.length;
                const processedResults = results.map(item => {
                    const start = new Date(item.start);
                    const end = new Date(item.end);
                    const diffMs = end.getTime() - start.getTime();
                    const diffMinutes = Math.floor(diffMs / (1000 * 60));
                    return {
                        ...useVenueWallClockForDisplay(item),
                        durationHours: `${diffMinutes}m`
                    };
                });
                this.dataSource.data = processedResults;
                this.totalItems = total;
                this.cdr.markForCheck();
            },
            error: (error) => {
                this.isLoading = false;
                this.hasSearched = true;
                console.error('Error fetching bookings:', error);
            }
        });
    }

    onPageChange(event: PageEvent): void {
        this.pageSize = event.pageSize;
        this.pageIndex = event.pageIndex;
        this.onSubmit();
    }

    openDialog(booking: any): void {
        const dialogRef = this.dialog.open(BookingComponent, {
            ...BOOKING_DIALOG_CONFIG,
            data: {
                booking: booking,
                courts: this.courts,
                venues: this.venues
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.onSubmit();
            }
        });
    }

    viewDetails(booking: any): void {
        console.log('View details for booking:', booking);
        this.bookingService.getBookingById(booking._id || booking.bookingId).subscribe({
            next: (bookingData: any) => {
                this.openDialog(bookingData);
            },
            error: (error) => {
                console.error('Error fetching booking details:', error);
                this.openDialog(booking);
            }
        });
    }

    downloadReport(): void {
        this.isLoading = true;
        const formData = this.reportForm.value;

        const exportFormData = {
            ...formData,
            page: this.pageIndex + 1,
            limit: this.pageSize
        };

        this.reportService.exportBookingsToCSV(exportFormData).subscribe({
            next: (response: Blob) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const csvText = reader.result as string;
                    const filteredCSV = this.filterCSVColumns(csvText);
                    
                    const blob = new Blob([filteredCSV], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `bookings-report-${new Date().toISOString().split('T')[0]}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    
                    this.isLoading = false;
                    this.cdr.markForCheck();
                };
                
                reader.onerror = () => {
                    this.isLoading = false;
                    this.cdr.markForCheck();
                    console.error('Error reading CSV file');
                };
                
                reader.readAsText(response);
            },
            error: (error) => {
                this.isLoading = false;
                this.cdr.markForCheck();
                console.error('Error downloading report:', error);
            }
        });
    }

    private filterCSVColumns(csvText: string): string {
        const lines = csvText.split('\n');
        if (lines.length === 0) return '';

        const headers = lines[0].split(',');
        const requiredColumns = ['bookingId', 'venue Name', 'court Name', 'start', 'durationHours', 'pricePerHour', 'status'];
        
        const columnIndices = requiredColumns.map(col => {
            const index = headers.findIndex(header => 
                header.trim().toLowerCase() === col.toLowerCase() || 
                header.trim().toLowerCase().includes(col.toLowerCase())
            );
            return index;
        }).filter(index => index !== -1);

        const filteredLines = lines.map(line => {
            const values = line.split(',');
            const filteredValues = columnIndices.map(index => values[index] || '');
            return filteredValues.join(',');
        });

        return filteredLines.join('\n');
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'confirmed':
                return 'bg-green-100 text-green-800';
            case 'tentative':
                return 'bg-yellow-100 text-yellow-800';
            case 'cancelled':
            case 'canceled':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    }

    getStatusIcon(status: string): string {
        switch (status) {
            case 'confirmed':
                return 'check_circle';
            case 'tentative':
                return 'help';
            case 'cancelled':
            case 'canceled':
                return 'cancel';
            default:
                return 'help';
        }
    }

    editBooking(booking: any): void {
        console.log('Edit booking:', booking);
        this.viewDetails(booking);
    }

    getVenueName(venueId: any): string {
        if (venueId && typeof venueId === 'object' && venueId.name) {
            return venueId.name;
        }
        const venue = this.venues.find(v => v._id === venueId || v.id === venueId);
        return venue ? (venue.name || venue.venueName || venue._id) : venueId;
    }

    getCourtName(courtId: any): string {
        if (courtId && typeof courtId === 'object' && courtId.name) {
            return courtId.name;
        }
        const court = this.courts.find(c => c._id === courtId || c.id === courtId);
        return court ? (court.name || court.courtName || court.title || court._id) : courtId;
    }
}
